import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCrm } from "@/hooks/useCrm";
import { api, ApiError } from "@/lib/api";
import { AuthLayout, Field } from "./AuthLayout";

// Aanmelden (contract §6.1): e-mail + wachtwoord, daarna eventueel de 6-cijferige code,
// en "Wachtwoord vergeten?" (altijd hetzelfde antwoord, ook voor een onbekend adres).

type Step = "login" | "code" | "forgot";

export function LoginPage() {
  const crm = useCrm();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; tone?: "yellow" | "blush" } | null>(null);

  const enter = async () => {
    await crm.reload();
    navigate("/", { replace: true });
  };

  const submit = (run: () => Promise<void>) => async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await run();
    } catch (error) {
      setMessage({ text: error instanceof Error ? error.message : String(error), tone: "blush" });
      if (error instanceof ApiError && error.code === "unauthenticated" && step === "code") setStep("login");
    } finally {
      setBusy(false);
    }
  };

  const go = (next: Step) => {
    setMessage(null);
    setStep(next);
  };

  return (
    <AuthLayout subtitle="CRM · aanmelden" message={message}>
      {step === "login" && (
        <form
          onSubmit={submit(async () => {
            setMessage(null);
            const result = await api<{ twoFactorRequired: boolean }>("auth/login", { body: { email, password }, quiet401: true });
            if (result.twoFactorRequired) go("code");
            else await enter();
          })}
        >
          <Field label="E-mail" htmlFor="email">
            <Input id="email" type="email" autoComplete="username" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Field label="Wachtwoord" htmlFor="password">
            <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <div className="mt-1.5 flex items-center justify-between gap-2.5">
            <Button variant="link" onClick={() => go("forgot")}>
              Wachtwoord vergeten?
            </Button>
            <Button type="submit" variant="default" disabled={busy}>
              Aanmelden
            </Button>
          </div>
        </form>
      )}

      {step === "code" && (
        <form
          onSubmit={submit(async () => {
            await api("auth/2fa/verify", { body: { code }, quiet401: true });
            await enter();
          })}
        >
          <p className="mb-2 text-[13px] text-muted-foreground">Geef de 6-cijferige code uit je authenticator-app.</p>
          <Field label="Code" htmlFor="code">
            <Input id="code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} autoComplete="one-time-code" required autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
          </Field>
          <div className="mt-1.5 flex items-center justify-between gap-2.5">
            <Button variant="link" onClick={() => go("login")}>
              Terug
            </Button>
            <Button type="submit" variant="default" disabled={busy}>
              Bevestigen
            </Button>
          </div>
        </form>
      )}

      {step === "forgot" && (
        <form
          onSubmit={submit(async () => {
            const result = await api<{ message: string }>("auth/forgot-password", { body: { email }, quiet401: true });
            setStep("login");
            setMessage({ text: result.message });
          })}
        >
          <p className="mb-2 text-[13px] text-muted-foreground">Je krijgt een link om zelf een nieuw wachtwoord in te stellen.</p>
          <Field label="E-mail" htmlFor="forgotEmail">
            <Input id="forgotEmail" type="email" autoComplete="username" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <div className="mt-1.5 flex items-center justify-between gap-2.5">
            <Button variant="link" onClick={() => go("login")}>
              Terug
            </Button>
            <Button type="submit" variant="default" disabled={busy}>
              Link versturen
            </Button>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
